// src/services/studentService.js
// ---------------------------------------------------------------------------
// CRUD de alunos. A criação do usuário em Firebase Auth é feita em um
// *app Firebase secundário* para não derrubar a sessão do professor/admin.
// As gravações iniciais em Firestore são feitas pelo Firestore do app
// secundário (tempDb), para que request.auth.uid == uid do aluno criado,
// atendendo às regras: only-owner create/read/update.
// ---------------------------------------------------------------------------

import { db } from '../firebase.js';

import {
  collection,
  doc,
  setDoc,
  getDocs,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  getDoc,
  updateDoc,
  limit,
  getFirestore as initDb, // Firestore do app secundário
} from 'firebase/firestore';

import {
  getApp,                // app principal já inicializado
  initializeApp as initApp,
} from 'firebase/app';

import {
  getAuth as initAuth,   // Auth do app secundário
  createUserWithEmailAndPassword,
  deleteUser,
  signOut,
  getIdToken,
} from 'firebase/auth';

/* ───────────── Referências (app principal) ───────────── */
const studentsCollectionRef = collection(db, 'students');

/* ───────────── Config ───────────── */
const DEFAULT_INITIAL_CREDITS = 5;

/* ───────────── Listagem ───────────── */
export const getAllStudents = async (classroomId = null) => {
  try {
    let q;
    if (classroomId) {
      // Evita índice composto: sem orderBy quando há where
      q = query(studentsCollectionRef, where('classroomId', '==', classroomId));
    } else {
      q = query(studentsCollectionRef, orderBy('name'));
    }

    const snap = await getDocs(q);
    let students = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

    // Ordena client-side se filtrou por classroomId
    if (classroomId) {
      students.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    }
    return students;
  } catch (err) {
    console.error('Error fetching students:', err);
    throw new Error('Failed to fetch students from Firestore.');
  }
};

export const getUnassignedStudents = async () => {
  try {
    const q = query(studentsCollectionRef, where('classroomId', '==', null));
    const snap = await getDocs(q);
    return snap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  } catch (error) {
    console.error('Error fetching unassigned students:', error);
    throw new Error('Failed to fetch unassigned students.');
  }
};

/* ───────────── Adição (Auth + Firestore) ─────────────
   Agora aceita `initialCredits` e grava os campos de crédito SOMENTE em users/{uid}.
*/
export const addStudent = async ({
  email,
  password,
  name,
  phone,               // optional "(99) 99999-9999"
  enrollmentNumber,    // optional
  classroomId,         // optional (auto-pick default/first if not provided)
  initialCredits,      // total de créditos inicial (opcional; default 2)
}) => {
  if (!email || !password || !name) {
    throw new Error('Missing required student data.');
  }

  // normaliza créditos iniciais
  let creditsInit = Number.isFinite(+initialCredits)
    ? Math.max(0, Math.floor(+initialCredits))
    : DEFAULT_INITIAL_CREDITS;

  // 1) Clona a config do app principal e cria app/Auth/DB temporários
  const primaryOpts = getApp().options;
  const tempApp  = initApp(primaryOpts, `temp-admin-${Date.now()}`);
  const tempAuth = initAuth(tempApp);
  const tempDb   = initDb(tempApp);

  let createdUser = null;

  try {
    // 2) Cria usuário no Auth do app temporário (isso autentica esse app)
    const cred = await createUserWithEmailAndPassword(tempAuth, email, password);
    createdUser = cred.user;
    const uid   = createdUser.uid;

    // 3) Garante token fresco para o Firestore do tempApp (request.auth)
    await getIdToken(createdUser, /* forceRefresh */ true);

    // 4) Resolve a sala final (usa main db para leitura)
    let finalClassroomId = classroomId || null;
    if (!finalClassroomId) {
      const defSnap = await getDocs(
        query(collection(db, 'classrooms'), where('isDefault', '==', true), limit(1))
      );
      if (!defSnap.empty) {
        finalClassroomId = defSnap.docs[0].id;
      } else {
        const firstSnap = await getDocs(
          query(collection(db, 'classrooms'), orderBy('name'), limit(1))
        );
        if (!firstSnap.empty) {
          finalClassroomId = firstSnap.docs[0].id;
        } else {
          throw new Error('No classrooms available. Please create a classroom first.');
        }
      }
    }

    const now = serverTimestamp();

    // 5) Escreve *pelo tempDb* para satisfazer regras (owner create)
    // users/{uid} — inclui créditos iniciais (fonte única da verdade)
    await setDoc(doc(tempDb, 'users', uid), {
      name,
      email,
      role: 'student',
      phone: phone ?? null,
      createdAt: now,
      // ── Créditos (SOMENTE em users) ──
      credits: creditsInit,           // créditos restantes
      creditsTotal: creditsInit,      // total concedido na criação
      creditsUsed: 0,                 // começa em 0
      creditsUpdatedAt: now,
    });

    // students/{uid} — sem qualquer campo de crédito
    await setDoc(doc(tempDb, 'students', uid), {
      name,
      email,
      phone: phone ?? null,
      enrollmentNumber: enrollmentNumber ?? null,
      classroomId: finalClassroomId,
      createdAt: now,
      articles: [],
    });

    return uid;
  } catch (err) {
    console.error('Error adding student:', err);

    // rollback do Auth se Firestore falhar
    if (createdUser) {
      try { await deleteUser(createdUser); } catch (_) { /* noop */ }
    }

    if (err.code === 'auth/email-already-in-use') {
      throw new Error('This email address is already registered.');
    }
    if (err.code === 'auth/weak-password') {
      throw new Error('Password is too weak (min 6 characters).');
    }
    throw new Error('Failed to add student. Check console for details.');
  } finally {
    // 6) Encerra app temporário (logout + delete)
    try { if (tempAuth.currentUser) await signOut(tempAuth); } catch (_) {}
    try { await tempApp.delete(); } catch (_) {}
  }
};



/* ───────────── Exclusão (Firestore docs) ─────────────
   Atenção: deletar o usuário no Auth exige backend (Admin SDK) ou
   ser o próprio usuário autenticado recentement. Aqui removemos
   apenas os documentos. */
export const deleteStudent = async (studentId) => {
  try {
    await Promise.all([
      deleteDoc(doc(db, 'students', studentId)),
      deleteDoc(doc(db, 'users', studentId)),
    ]);
    console.warn(
      `Firestore docs for student ${studentId} deleted. Auth user NOT deleted on client.`
    );
  } catch (err) {
    console.error('Error deleting student Firestore data:', err);
    throw new Error('Failed to delete student data from Firestore.');
  }
};

export const assignStudentToClassroom = async (studentId, classroomId) => {
  if (!studentId || !classroomId) {
    throw new Error('Student ID and classroom ID are required.');
  }

  try {
    await updateDoc(doc(db, 'students', studentId), {
      classroomId,
    });
  } catch (error) {
    console.error(`Error assigning student ${studentId} to classroom ${classroomId}:`, error);
    throw new Error('Failed to assign student to classroom.');
  }
};

export const removeStudentFromClassroom = async (studentId) => {
  if (!studentId) {
    throw new Error('Student ID is required.');
  }

  try {
    await updateDoc(doc(db, 'students', studentId), {
      classroomId: null,
    });
  } catch (error) {
    console.error(`Error removing student ${studentId} from classroom:`, error);
    throw new Error('Failed to remove student from classroom.');
  }
};

/* ───────────── Leitura por ID ───────────── */
export const getStudentById = async (studentId) => {
  try {
    const snap = await getDoc(doc(db, 'students', studentId));
    if (snap.exists()) return { id: snap.id, ...snap.data() };
    return null;
  } catch (err) {
    if (err.code === 'permission-denied') {
      console.warn('Rules blocked read of student', studentId);
      return null;
    }
    throw err;
  }
};

/* ───────────── Atualização ───────────── */
export const updateStudent = async (studentId, updatedData) => {
  try {
    // Campos que também espelhamos em users/{uid}
    const common = {};
    if (Object.prototype.hasOwnProperty.call(updatedData, 'name')) {
      common.name = updatedData.name;
    }
    if (Object.prototype.hasOwnProperty.call(updatedData, 'email')) {
      common.email = updatedData.email;
    }

    // Atualiza users/{uid} se houver campos comuns (NÃO mexe em créditos aqui)
    if (Object.keys(common).length > 0) {
      await updateDoc(doc(db, 'users', studentId), {
        ...common,
        updatedAt: serverTimestamp(),
      });
    }

    // Atualiza students/{uid} (NÃO grava qualquer campo de crédito aqui)
    await updateDoc(doc(db, 'students', studentId), {
      ...updatedData,
      updatedAt: serverTimestamp(),
    });
  } catch (err) {
    console.error('Error updating student:', err);
    throw new Error('Failed to update student in Firestore.');
  }
};

/* ───────────── Artigos ───────────── */
export const addArticleToStudent = async (studentId, articleId) => {
  try {
    const ref  = doc(db, 'students', studentId);
    const snap = await getDoc(ref);
    if (!snap.exists()) throw new Error('Student not found');

    const articles = snap.data().articles || [];
    if (!articles.includes(articleId)) {
      await updateDoc(ref, { articles: [...articles, articleId] });
    }
  } catch (err) {
    console.error('Error adding article:', err);
    throw new Error('Failed to add article to student.');
  }
};

export const removeArticleFromStudent = async (studentId, articleId) => {
  try {
    const ref  = doc(db, 'students', studentId);
    const snap = await getDoc(ref);
    if (!snap.exists()) throw new Error('Student not found');

    const updated = (snap.data().articles || []).filter((id) => id !== articleId);
    await updateDoc(ref, { articles: updated });
  } catch (err) {
    console.error('Error removing article:', err);
    throw new Error('Failed to remove article from student.');
  }
};
